<template>
  <v-container fluid class="dl">
    <Breadcrumbs />

    <!-- Шапка -->
    <header class="dl-hero">
      <div class="dl-hero__eyebrow">
        <v-icon size="18" color="#ff9100" class="mr-1">
          mdi-tray-arrow-down
        </v-icon>
        База знаний
      </div>
      <h1 class="dl-hero__title">
        Загрузки
      </h1>
      <p class="dl-hero__lead">
        Мобильные приложения для сотрудников, установщик кассового модуля F.A.K.T.
        и требования к оборудованию филиала.
      </p>
    </header>

    <!-- Мобильные приложения -->
    <section class="dl-section">
      <div class="dl-section__head">
        <span class="dl-section__title">Мобильные приложения</span>
      </div>

      <div class="dl-apps">
        <article
          v-for="(app, ai) in apps"
          :key="app.name"
          class="dl-app"
          :style="{ animationDelay: (ai * 70) + 'ms' }"
        >
          <div class="dl-app__top">
            <div class="dl-app__badge">
              <v-icon size="30" color="#fff">
                {{ app.icon }}
              </v-icon>
            </div>
            <div class="dl-app__meta">
              <h3 class="dl-app__name">
                {{ app.name }}
              </h3>
              <div class="dl-app__platform">
                <v-icon size="15" color="#8a97a6" class="mr-1">
                  {{ app.platformIcon }}
                </v-icon>
                {{ app.platform }}
              </div>
            </div>
          </div>

          <div class="dl-app__stores">
            <div v-for="store in app.stores" :key="store.label" class="dl-store">
              <a
                :href="store.url"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-store__btn"
              >
                <v-icon size="22" class="dl-store__icon">
                  {{ store.icon }}
                </v-icon>
                <span class="dl-store__label">
                  <span class="dl-store__hint">Открыть в</span>
                  <span class="dl-store__name">{{ store.label }}</span>
                </span>
                <v-icon size="17" class="dl-store__arrow">
                  mdi-open-in-new
                </v-icon>
              </a>
              <button
                type="button"
                class="dl-store__copy"
                :class="{ 'dl-store__copy--done': copiedKey === store.url }"
                :title="copiedKey === store.url ? 'Скопировано' : 'Скопировать ссылку'"
                @click="copyLink(store.url)"
              >
                <v-icon size="19">
                  {{ copiedKey === store.url ? 'mdi-check' : 'mdi-content-copy' }}
                </v-icon>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- Кассовый модуль -->
    <section class="dl-section">
      <div class="dl-section__head">
        <span class="dl-section__title">Кассовый модуль</span>
      </div>

      <article class="dl-cash" :style="{ animationDelay: '140ms' }">
        <div class="dl-cash__main">
          <div class="dl-app__top">
            <div class="dl-app__badge dl-app__badge--cash">
              <v-icon size="30" color="#fff">
                mdi-cash-register
              </v-icon>
            </div>
            <div class="dl-app__meta">
              <h3 class="dl-app__name">
                {{ cashModule.name }}
              </h3>
              <div
                class="dl-app__platform"
                :class="{ 'dl-cash__pending': releaseState === 'loading' }"
              >
                <v-icon size="15" color="#8a97a6" class="mr-1">
                  mdi-microsoft-windows
                </v-icon>
                {{ cashPlatformLabel }}
              </div>
            </div>
          </div>

          <a
            v-if="releaseState !== 'missing'"
            :href="releaseState === 'ready' ? release.url : null"
            :aria-disabled="releaseState === 'loading' ? 'true' : null"
            class="dl-cash__install"
            :class="{ 'dl-cash__install--loading': releaseState === 'loading' }"
          >
            <v-icon size="20" color="#fff" class="mr-2">
              mdi-tray-arrow-down
            </v-icon>
            Скачать установщик
          </a>
          <p v-else class="dl-cash__missing">
            Установщик пока не опубликован
          </p>
        </div>

        <div class="dl-cash__extras">
          <div
            v-for="extra in cashModule.extras"
            :key="extra.title"
            class="dl-cash__extras-block"
          >
            <div class="dl-cash__extras-head">
              <v-icon size="18" color="#c48a12" class="mr-2">
                mdi-alert-circle-outline
              </v-icon>
              {{ extra.title }}
            </div>
            <p class="dl-cash__extras-note">
              {{ extra.note }}
            </p>
            <div class="dl-cash__extras-links">
              <a
                v-for="item in extra.items"
                :key="item.label"
                :href="item.url"
                :target="item.external ? '_blank' : null"
                :rel="item.external ? 'noopener noreferrer' : null"
                class="dl-cash__extra"
              >
                <v-icon size="17" class="mr-1">
                  {{ item.external ? 'mdi-open-in-new' : 'mdi-tray-arrow-down' }}
                </v-icon>
                {{ item.label }}
              </a>
            </div>
          </div>
        </div>
      </article>
    </section>

    <!-- Требования к оборудованию -->
    <section id="hardware" class="dl-section dl-spec-section">
      <div class="dl-section__head">
        <span class="dl-section__title">{{ hardwareSpec.title }}</span>
      </div>
      <p class="dl-spec-lead">
        {{ hardwareSpec.lead }}
      </p>

      <div class="dl-spec-grid">
        <article
          v-for="(place, pi) in hardwareSpec.workplaces"
          :key="place.key"
          class="dl-spec"
          :class="{ 'dl-spec--wide': place.wide }"
          :style="{ animationDelay: (210 + pi * 70) + 'ms' }"
        >
          <div class="dl-app__top dl-spec__top">
            <div
              class="dl-app__badge"
              :class="{ 'dl-app__badge--cash': place.tone === 'dark' }"
            >
              <v-icon size="30" color="#fff">
                {{ place.icon }}
              </v-icon>
            </div>
            <div class="dl-app__meta">
              <h3 class="dl-app__name">
                {{ place.title }}
              </h3>
            </div>
          </div>

          <dl class="dl-spec__list">
            <div
              v-for="item in place.items"
              :key="item.name"
              class="dl-spec__row"
            >
              <dt class="dl-spec__name">
                {{ item.name }}
              </dt>
              <dd class="dl-spec__body">
                <p class="dl-spec__req">
                  {{ item.requirement }}
                </p>
                <ul v-if="item.details" class="dl-spec__details">
                  <li v-for="(line, li) in item.details" :key="li">
                    {{ line }}
                  </li>
                </ul>
                <p v-if="item.recommended" class="dl-spec__hint">
                  <v-icon size="16" color="#c48a12" class="dl-spec__hint-icon">
                    mdi-lightbulb-outline
                  </v-icon>
                  <span>Рекомендуем: {{ item.recommended }}</span>
                </p>
                <span v-if="item.verified" class="dl-spec__verified">
                  <v-icon size="15" color="#2e7d32" class="mr-1">
                    mdi-check-decagram-outline
                  </v-icon>
                  Проверено в сети: {{ item.verified }}
                </span>
              </dd>
            </div>
          </dl>
        </article>
      </div>

      <p class="dl-spec-source">
        {{ hardwareSpec.source.text }}
        <a
          :href="hardwareSpec.source.url"
          target="_blank"
          rel="noopener noreferrer"
        >{{ hardwareSpec.source.label }}</a>
      </p>
    </section>
  </v-container>
</template>

<script>
  import { downloadsApps, cashModule, hardwareSpec } from '@/pages/KnowledgeBase/config'
  import downloadsApi from '@/api/downloads'

  export default {
    name: 'KnowledgeBaseDownloads',
    data () {
      return {
        apps: downloadsApps,
        cashModule,
        hardwareSpec,
        copiedKey: null,
        copiedTimer: null,
        // Релиз кассового модуля с бэка: loading -> ready | missing
        release: null,
        releaseState: 'loading',
      }
    },
    computed: {
      cashPlatformLabel () {
        if (this.releaseState === 'ready') return `Windows · версия ${this.release.version}`
        if (this.releaseState === 'loading') return 'Windows · версия …'
        return 'Windows'
      },
    },
    created () {
      this.loadCashRelease()
    },
    mounted () {
      this.scrollToHash()
    },
    beforeDestroy () {
      if (this.copiedTimer) clearTimeout(this.copiedTimer)
    },
    methods: {
      async loadCashRelease () {
        try {
          const release = await downloadsApi.fetchCashModule()
          this.release = release
          this.releaseState = release && release.url ? 'ready' : 'missing'
        } catch (e) {
          // 404 - релиз не опубликован; сеть или 5xx - показываем то же, страница работает
          this.release = null
          this.releaseState = 'missing'
        }
      },
      scrollToHash () {
        const id = (this.$route.hash || '').replace('#', '')
        if (!id) return
        this.$nextTick(() => {
          const el = document.getElementById(id)
          if (el) el.scrollIntoView({ block: 'start' })
        })
      },
      async copyLink (url) {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(url)
          } else {
            const ta = document.createElement('textarea')
            ta.value = url
            ta.style.position = 'fixed'
            ta.style.opacity = '0'
            document.body.appendChild(ta)
            ta.select()
            document.execCommand('copy')
            document.body.removeChild(ta)
          }
          this.copiedKey = url
          if (this.copiedTimer) clearTimeout(this.copiedTimer)
          this.copiedTimer = setTimeout(() => { this.copiedKey = null }, 1600)
          this.showSuccessMessage('Ссылка скопирована')
        } catch (e) {
          this.showErrorMessage('Не удалось скопировать ссылку')
        }
      },
    },
  }
</script>

<style scoped lang="less">
@primary: #ff9100;
@ink: #1f2530;
@ink-muted: #6d7684;
@line: #e6e8ec;

.dl {
  max-width: 980px;
  margin: 0;
  padding-bottom: 56px;
}

/* Шапка */
.dl-hero {
  padding: 6px 0 20px;
  animation: dl-rise 0.5s ease both;

  &__eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: @primary;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 30px;
    font-weight: 400;
    line-height: 1.15;
    color: @ink;
    margin: 0 0 8px;
  }

  &__lead {
    max-width: 640px;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.6;
    color: @ink-muted;
    margin: 0;
  }
}

/* Секция */
.dl-section {
  margin-top: 28px;

  &__head {
    border-bottom: 2px solid @primary;
    padding: 4px 0;
    margin-bottom: 18px;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: @ink;
  }
}

/* Карточки приложений */
.dl-apps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 18px;
}

.dl-app {
  padding: 20px;
  background: #fff;
  border: 1px solid @line;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(15, 20, 30, 0.05);
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  animation: dl-rise 0.5s ease both;

  &:hover {
    box-shadow: 0 10px 26px rgba(15, 20, 30, 0.1);
    transform: translateY(-2px);
    border-color: fade(@primary, 40%);
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
  }

  &__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    border-radius: 15px;
    background: linear-gradient(135deg, #ffab3d 0%, #ff9100 100%);
    box-shadow: 0 6px 14px rgba(255, 145, 0, 0.32);
    flex: 0 0 auto;

    &--cash {
      background: linear-gradient(135deg, #566172 0%, #2f3542 100%);
      box-shadow: 0 6px 14px rgba(47, 53, 66, 0.28);
    }
  }

  &__name {
    font-size: 18px;
    font-weight: 500;
    color: @ink;
    margin: 0 0 2px;
    line-height: 1.25;
  }

  &__platform {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 400;
    color: #8a97a6;
  }

  &__stores {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

/* Строка стора: кнопка загрузки + копирование */
.dl-store {
  display: flex;
  gap: 8px;

  &__btn {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    padding: 10px 14px;
    border: 1px solid @line;
    border-radius: 11px;
    background: #fff;
    text-decoration: none;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: @primary;
      background: fade(@primary, 6%);
      box-shadow: 0 3px 10px rgba(255, 145, 0, 0.14);
    }
  }

  &__icon {
    color: @ink;
    flex: 0 0 auto;
  }

  &__label {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__hint {
    font-size: 11px;
    color: @ink-muted;
  }

  &__name {
    font-size: 15px;
    font-weight: 500;
    color: @ink;
  }

  &__arrow {
    color: #b4bcc7;
    flex: 0 0 auto;
  }

  &__copy {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    border: 1px solid @line;
    border-radius: 11px;
    background: #fff;
    color: @ink-muted;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
      border-color: @primary;
      background: fade(@primary, 6%);
      color: darken(@primary, 6%);
    }

    &--done {
      border-color: #4caf50;
      background: fade(#4caf50, 10%);
      color: #4caf50;
    }
  }
}

/* Кассовый модуль */
.dl-cash {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 0;
  background: #fff;
  border: 1px solid @line;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(15, 20, 30, 0.05);
  animation: dl-rise 0.5s ease both;

  &__main {
    padding: 22px;
  }

  &__install {
    display: inline-flex;
    align-items: center;
    height: 46px;
    padding: 0 22px;
    margin-top: 4px;
    border-radius: 100px;
    background: @primary;
    color: #fff;
    font-size: 15px;
    font-weight: 500;
    text-decoration: none;
    box-shadow: 0 4px 12px rgba(255, 145, 0, 0.3);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 7px 18px rgba(255, 145, 0, 0.4);
    }
  }

  &__install--loading {
    opacity: 0.55;
    pointer-events: none;
    box-shadow: none;
  }

  &__pending {
    opacity: 0.6;
  }

  &__missing {
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 400;
    color: @ink-muted;
  }

  &__extras {
    padding: 22px;
    background: #fbfaf7;
    border-left: 1px solid @line;
  }

  &__extras-block + &__extras-block {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid @line;
  }

  &__extras-head {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #8a6d1e;
    margin-bottom: 6px;
  }

  &__extras-note {
    font-size: 13px;
    font-weight: 300;
    line-height: 1.5;
    color: @ink-muted;
    margin: 0 0 12px;
  }

  &__extras-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  &__extra {
    display: inline-flex;
    align-items: center;
    padding: 7px 12px;
    border: 1px solid @line;
    border-radius: 9px;
    background: #fff;
    color: #5b6472;
    font-size: 13px;
    font-weight: 400;
    text-decoration: none;
    transition: border-color 0.15s ease, color 0.15s ease;

    &:hover {
      border-color: @primary;
      color: darken(@primary, 8%);
    }
  }
}

/* Требования к оборудованию */
.dl-spec-section {
  scroll-margin-top: 100px;
}

.dl-spec-lead {
  max-width: 640px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.55;
  color: @ink-muted;
  margin: -6px 0 18px;
}

.dl-spec-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.dl-spec {
  padding: 20px;
  background: #fff;
  border: 1px solid @line;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(15, 20, 30, 0.05);
  animation: dl-rise 0.5s ease both;

  &--wide {
    grid-column: ~"1 / -1";
  }

  &__top {
    margin-bottom: 6px;
  }

  &__list {
    margin: 0;
  }

  &__row {
    padding: 14px 0 0;
    margin-top: 14px;
    border-top: 1px solid #f0f1f3;

    .dl-spec--wide & {
      display: grid;
      grid-template-columns: 168px minmax(0, 1fr);
      gap: 16px;
    }
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    color: @ink-muted;
    margin: 0 0 4px;

    .dl-spec--wide & {
      margin: 0;
      padding-top: 1px;
    }
  }

  &__body {
    margin: 0;
    min-width: 0;
  }

  &__req {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.55;
    color: @ink;
    margin: 0;
  }

  &__details {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    font-size: 13px;
    font-weight: 300;
    line-height: 1.5;
    color: @ink-muted;

    li + li {
      margin-top: 2px;
    }
  }

  &__hint {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 8px 0 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.5;
    color: #8a6d1e;
  }

  &__hint-icon {
    flex: 0 0 auto;
    margin-top: 2px;
  }

  &__verified {
    display: inline-flex;
    align-items: center;
    margin-top: 10px;
    padding: 4px 10px 4px 8px;
    border-radius: 100px;
    background: #e8f5e9;
    color: #2e7d32;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
  }
}

.dl-spec-source {
  margin: 16px 0 0;
  font-size: 13px;
  font-weight: 300;
  color: @ink-muted;

  a {
    color: @primary;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@keyframes dl-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .dl-spec-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .dl-hero__title {
    font-size: 24px;
  }

  .dl-cash {
    grid-template-columns: 1fr;
  }

  .dl-cash__extras {
    border-left: none;
    border-top: 1px solid @line;
  }

  .dl-spec-grid {
    grid-template-columns: 1fr;
  }

  .dl-spec--wide .dl-spec__row {
    display: block;
  }

  .dl-spec--wide .dl-spec__name {
    margin: 0 0 4px;
    padding-top: 0;
  }
}
</style>
